/**
 * Smart unit scaling utility for charts
 * Determines the most appropriate unit based on the 75th percentile of data values
 */

export type UnitScale = 'KB' | 'MB' | 'GB' | 'TB'

interface UnitConfig {
  unit: UnitScale
  multiplier: number
  threshold: number
}

const UNIT_CONFIGS: UnitConfig[] = [
  { unit: 'TB', multiplier: 1024 * 1024 * 1024 * 1024, threshold: 100 * 1024 * 1024 * 1024 }, // 100GB threshold
  { unit: 'GB', multiplier: 1024 * 1024 * 1024, threshold: 100 * 1024 * 1024 }, // 100MB threshold
  { unit: 'MB', multiplier: 1024 * 1024, threshold: 100 * 1024 }, // 100KB threshold
  { unit: 'KB', multiplier: 1024, threshold: 1 }, // 1KB threshold
]

/**
 * Determines the optimal unit scale based on the 75th percentile of values
 * @param values Array of numeric values to analyze
 * @param baseUnit The base unit of the input values (e.g., 'bytes', 'MB')
 * @returns Object containing the optimal unit and conversion function
 */
export function getSmartUnitScale(
  values: number[],
  baseUnit: 'bytes' | 'MB' = 'bytes',
): {
  unit: UnitScale
  formatValue: (value: number) => string
  axisLabel: string
} {
  if (!values.length) {
    return {
      unit: 'GB',
      formatValue: (value: number) => {
        // Smart decimal places for GB unit (fallback case)
        const thresholds = { high: 10, medium: 1, low: 0.1, veryLow: 0.01 }
        let decimalPlaces: number

        if (value >= thresholds.high) {
          decimalPlaces = value >= thresholds.high * 10 ? 0 : 1
        } else if (value >= thresholds.medium) {
          decimalPlaces = value >= thresholds.medium * 5 ? 1 : 2
        } else if (value >= thresholds.low) {
          // For GB unit, use 2 decimal places for medium values
          decimalPlaces = 2
        } else if (value >= thresholds.veryLow) {
          // For GB unit, use 2-3 decimal places for small values
          decimalPlaces = value >= thresholds.veryLow * 5 ? 2 : 3
        } else {
          // For GB unit, use 3-4 decimal places for very small values
          decimalPlaces = value >= 0.001 ? 3 : 4
        }

        if (value === 0) decimalPlaces = 0

        const formatted = value.toFixed(decimalPlaces)
        const trimmed = parseFloat(formatted).toString()

        return `${trimmed}GB`
      },
      axisLabel: 'Storage (GB)',
    }
  }

  // Filter out zero values and convert to base unit (bytes)
  const nonZeroValues = values
    .filter(value => value > 0)
    .map(value => {
      // Convert to bytes if needed
      if (baseUnit === 'MB') {
        return value * 1024 * 1024 // Convert MB to bytes
      }

      return value
    })

  if (!nonZeroValues.length) {
    return {
      unit: 'GB',
      formatValue: (value: number) => {
        // Smart decimal places for GB unit (fallback case)
        const thresholds = { high: 10, medium: 1, low: 0.1, veryLow: 0.01 }
        let decimalPlaces: number

        if (value >= thresholds.high) {
          decimalPlaces = value >= thresholds.high * 10 ? 0 : 1
        } else if (value >= thresholds.medium) {
          decimalPlaces = value >= thresholds.medium * 5 ? 1 : 2
        } else if (value >= thresholds.low) {
          // For GB unit, use 2 decimal places for medium values
          decimalPlaces = 2
        } else if (value >= thresholds.veryLow) {
          // For GB unit, use 2-3 decimal places for small values
          decimalPlaces = value >= thresholds.veryLow * 5 ? 2 : 3
        } else {
          // For GB unit, use 3-4 decimal places for very small values
          decimalPlaces = value >= 0.001 ? 3 : 4
        }

        if (value === 0) decimalPlaces = 0

        const formatted = value.toFixed(decimalPlaces)
        const trimmed = parseFloat(formatted).toString()

        return `${trimmed}GB`
      },
      axisLabel: 'Storage (GB)',
    }
  }

  // Calculate 75th percentile
  const sortedValues = [...nonZeroValues].sort((a, b) => a - b)
  const percentile75Index = Math.floor(sortedValues.length * 0.75)
  const percentile75Value = sortedValues[percentile75Index]

  // Find the most appropriate unit
  const config =
    UNIT_CONFIGS.find(c => percentile75Value >= c.threshold) ||
    UNIT_CONFIGS[UNIT_CONFIGS.length - 1]

  return {
    unit: config.unit,
    formatValue: (value: number) => {
      const convertedValue = value / config.multiplier

      // Smart decimal places based on value size and unit scale
      let decimalPlaces: number

      // Define thresholds based on unit scale for better precision
      const thresholds = {
        TB: { high: 10, medium: 1, low: 0.1, veryLow: 0.01 },
        GB: { high: 10, medium: 1, low: 0.1, veryLow: 0.01 },
        MB: { high: 1000, medium: 100, low: 10, veryLow: 1 },
        KB: { high: 10000, medium: 1000, low: 100, veryLow: 10 },
      }

      const unitThresholds = thresholds[config.unit]

      if (convertedValue >= unitThresholds.high) {
        // Very large values: 0-1 decimal place
        decimalPlaces = convertedValue >= unitThresholds.high * 10 ? 0 : 1
      } else if (convertedValue >= unitThresholds.medium) {
        // Large values: 1-2 decimal places
        decimalPlaces = convertedValue >= unitThresholds.medium * 5 ? 1 : 2
      } else if (convertedValue >= unitThresholds.low) {
        // Medium values: 2 decimal places for GB/TB, 2-3 for others
        if (config.unit === 'GB' || config.unit === 'TB') {
          decimalPlaces = 2
        } else {
          decimalPlaces = convertedValue >= unitThresholds.low * 5 ? 2 : 3
        }
      } else if (convertedValue >= unitThresholds.veryLow) {
        // Small values: 2-3 decimal places for GB/TB, 3-4 for others
        if (config.unit === 'GB' || config.unit === 'TB') {
          decimalPlaces = convertedValue >= unitThresholds.veryLow * 5 ? 2 : 3
        } else {
          decimalPlaces = convertedValue >= unitThresholds.veryLow * 5 ? 3 : 4
        }
      } else {
        // Very small values: 3-4 decimal places for GB/TB, 4-6 for others
        if (config.unit === 'GB' || config.unit === 'TB') {
          decimalPlaces = convertedValue >= 0.001 ? 3 : 4
        } else {
          decimalPlaces = convertedValue >= 0.001 ? 4 : 6
        }
      }

      // Special case: if value is exactly 0, show 0 decimal places
      if (convertedValue === 0) {
        decimalPlaces = 0
      }

      // Remove trailing zeros
      const formatted = convertedValue.toFixed(decimalPlaces)
      const trimmed = parseFloat(formatted).toString()

      return `${trimmed}${config.unit}`
    },
    axisLabel:
      config.unit === 'KB' || config.unit === 'MB' || config.unit === 'GB' || config.unit === 'TB'
        ? `Storage (${config.unit})`
        : `Bandwidth (${config.unit})`,
  }
}

/**
 * Specialized function for bandwidth data
 */
export function getSmartBandwidthUnitScale(values: number[]): {
  unit: UnitScale
  formatValue: (value: number) => string
  axisLabel: string
} {
  const result = getSmartUnitScale(values, 'bytes') // Bandwidth values are now in bytes

  return {
    ...result,
    axisLabel: `Bandwidth (${result.unit})`,
  }
}
